import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');
  const [showAllChapters, setShowAllChapters] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.categoryLabel, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const displayedChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(0, 5);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://koreanmanhwa.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <title>{comic.title} - 韩国漫画网</title>
      <meta name="description" content={comic.description.slice(0, 160)} />
      <meta name="keywords" content={`韩国漫画网, 韩漫在线观看, ${comic.title}, ${comic.categoryLabel}, ${comic.tags.join(', ')}`} />
      <link rel="canonical" href={`https://koreanmanhwa.com/article/${comic.id}`} />
      <meta property="og:title" content={`${comic.title} - 韩国漫画网`} />
      <meta property="og:description" content={comic.description.slice(0, 160)} />
      <meta property="og:image" content={comic.cover} />
      <meta property="og:url" content={`https://koreanmanhwa.com/article/${comic.id}`} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${comic.title} - 韩国漫画网`} />
      <meta name="twitter:description" content={comic.description.slice(0, 160)} />
      <meta name="twitter:image" content={comic.cover} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-[200px] md:w-[240px] mx-auto lg:mx-0">
                  <div className="h-[280px] md:h-[340px] rounded-xl overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      title={`${comic.title} - 韩国漫画网`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {comic.isHot && (
                    <span className="px-2.5 py-0.5 text-[11px] font-medium bg-primary-500 text-white rounded-full">HOT</span>
                  )}
                  {comic.isNew && (
                    <span className="px-2.5 py-0.5 text-[11px] font-medium bg-accent-500 text-background-50 rounded-full">NEW</span>
                  )}
                  <span className={`px-2.5 py-0.5 text-[11px] font-medium rounded-full ${
                    comic.status === '连载中' ? 'bg-secondary-100 text-secondary-700' : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 text-[11px] bg-background-100 text-foreground-500 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 text-xs md:text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    作者：{comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-paint-brush-line"></i>
                    画师：{comic.artist}
                  </span>
                  <span className="flex items-center gap-1 text-accent-600">
                    <i className="ri-star-fill"></i>
                    {comic.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(comic.views)} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-4 border-b border-background-200/70 mb-4">
                    <button
                      onClick={() => setActiveTab('chapters')}
                      className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'chapters'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      章节列表 ({comic.chapters.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('info')}
                      className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'info'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      漫画简介
                    </button>
                  </div>

                  {activeTab === 'chapters' && (
                    <div>
                      {comic.chapters.length === 0 ? (
                        <p className="text-sm text-foreground-500 py-8 text-center">暂无章节</p>
                      ) : (
                        <>
                          <div className="space-y-1">
                            {displayedChapters.map((chapter) => (
                              <div
                                key={chapter.id}
                                className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                                    <span className="text-xs font-medium text-primary-700">{chapter.number}</span>
                                  </div>
                                  <span className="text-sm text-foreground-800">{chapter.title}</span>
                                </div>
                                <span className="text-[11px] text-foreground-400">{chapter.updatedAt}</span>
                              </div>
                            ))}
                          </div>
                          {comic.chapters.length > 5 && (
                            <button
                              onClick={() => setShowAllChapters(!showAllChapters)}
                              className="mt-4 w-full py-2.5 text-sm text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg transition-colors cursor-pointer"
                            >
                              {showAllChapters
                                ? <><span>收起</span><i className="ml-1 ri-arrow-up-s-line"></i></>
                                : <><span>查看全部 {comic.chapters.length} 话</span><i className="ml-1 ri-arrow-down-s-line"></i></>
                              }
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'info' && (
                    <div>
                      <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                        {comic.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="flex gap-4 md:gap-5 overflow-x-auto pb-2" data-product-shop>
                  {relatedComics.map((c) => (
                    <div key={c.id} className="flex-shrink-0">
                      <ComicCard comic={c} size="normal" />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}